import React from 'react';
import getCountryISO3 from 'country-iso-2-to-3';
import useLocalStorageState from '../../hooks/use-local-storage-state';
import useNearbyFranchises from '../../hooks/use-nearby-franchises';
import type {
    UseNearbyFranchiseMultipleItems,
    UseNearbyFranchiseSingleItem,
} from '../../hooks/use-nearby-franchises';
import { SearchInputAddressComponents } from '../../components/franchise/franchise-search-input';
import { FranchiseData } from '../../sourcing/source-nodes/sources/franchises';
import { Announcement } from '../../utils/filter-announcements';
import { PHONE_NUMBER } from '../../constants';

let geoEdgePrefetch: Promise<Response> | null = null;

export const prefetchGeoEdge = (): Promise<Response> | null => {
    if (typeof window === 'undefined') {
        return null;
    }
    if (!geoEdgePrefetch) {
        geoEdgePrefetch = fetch('/geo/edge');
    }
    return geoEdgePrefetch;
};

const readPrefetchedGeoEdge = async (): Promise<unknown | null> => {
    const prefetch = prefetchGeoEdge();
    if (!prefetch) {
        return null;
    }
    try {
        const response = await prefetch;
        if (!response.ok) {
            return null;
        }
        return response.json();
    } catch {
        return null;
    }
};

const LOCAL_GEO_KEY = 'serv_geo_v4';

enum Provider {
    NETLIFY = 'netlify',
    IPSTACK = 'ipStack',
    NAVIGATOR = 'navigator',
    FRANCHISE = 'franchise',
}

const franchiseToGeo = (
    franchise: FranchiseData,
): UserLocationData | null => {
    if (!franchise) { return null; }

    if (
        franchise?.address?.city
        && typeof franchise?.areaServedLocationData?.latitude === 'number'
        && typeof franchise?.areaServedLocationData?.longitude === 'number'
        && franchise?.address?.state
        && franchise?.address?.countryCode
    ) {
        return {
            provider: Provider.FRANCHISE,
            city: franchise.address.city,
            state: franchise.address.state,
            stateShort: franchise.address.state,
            country: franchise.address.countryCode,
            latitude: franchise.areaServedLocationData.latitude,
            longitude: franchise.areaServedLocationData.longitude,
            franchiseId: String(franchise.franchiseNumber),
            zip: franchise?.address?.postalCode || '',
        };
    }

    return null;
};

interface UserLocationData {
    provider: Provider
    city: string
    state: string
    stateShort: string
    country: string
    latitude: number
    longitude: number
    zip?: string
    // This should only be used for User Set queries
    franchiseId: string | null
}

interface UserLocationDataSet {
    geolocationApi: UserLocationData | null
    navigator: UserLocationData | null
    userSelectedFranchiseLocation: UserLocationData | null
    userSelectedGeoLocation: UserLocationData | null
    locationChangedByUser: boolean
}

interface IpStackResponse {
    ip: string;
    type: string;
    continent_code: string;
    continent_name: string;
    country_code: string;
    country_name: string;
    region_code: string;
    region_name: string;
    city: string;
    zip: string;
    latitude: number;
    longitude: number;
    msa: string | null;
    dma: string | null;
    radius: string;
    ip_routing_type: string;
    connection_type: string;
    location: {
        geoname_id: number;
        capital: string;
        languages: Array<{
            code: string;
            name: string;
            native: string;
        }>;
        country_flag: string;
        country_flag_emoji: string;
        country_flag_emoji_unicode: string;
        calling_code: string;
        is_eu: boolean;
    };
    time_zone: {
        id: string;
        current_time: string;
        gmt_offset: number;
        code: string;
        is_daylight_saving: boolean;
    };
    currency: {
        code: string;
        name: string;
        plural: string;
        symbol: string;
        symbol_native: string;
    };
}

const runIpStackQuery = async (): Promise<UserLocationData | null> => {
    let ipGeo = {};

    try {
        // eslint-disable-next-line no-restricted-syntax -- This existing geolocation provider intentionally calls the external IP service.
        ipGeo = await (await fetch('https://api.ipstack.com/check?access_key=796435b41d113bb2ce49c3598fff5fba')).json();
    } catch (e) {
        return null;
    }

    const {
        latitude, longitude, city, region_code, region_name, country_code,
    } = (ipGeo || {}) as IpStackResponse;

    // only relevant while we decide which location detection provider to use
    if (latitude && longitude) {
        return {
            provider: Provider.IPSTACK,
            latitude,
            longitude,
            city,
            stateShort: region_code,
            country: getCountryISO3(country_code) || country_code,
            state: region_name,
            franchiseId: null,
        };
    }
    return null;
};

type NetlifyGeolocation = {
    city?: string;
    country?: {
        code: string;
        name: string;
    };
    subdivision?: {
        code: string;
        name: string;
    };
    timezone?: string;
    latitude?: number;
    longitude?: number;
    postalCode?: string;
};
// once we choose a location detection provider (and the fallback) will delete the one we don't use
const runNetlifyQuery = async (): Promise<UserLocationData | null> => {
    let netlifyGeo = await readPrefetchedGeoEdge();

    if (!netlifyGeo) {
        try {
            netlifyGeo = await (await fetch('/geo/edge')).json();
        } catch (e) {
            return runIpStackQuery();
        }
    }

    const { latitude, longitude, city, subdivision, country } = (netlifyGeo || {}) as NetlifyGeolocation;
    // only relevant while we decide which location detection provider to use
    if (latitude && longitude) {
        // below is to overcome the scenario where netlify returns lat and long without a city (which, sometimes happens)
        let reverseGeocodeLocation;
        if (!city || !subdivision?.code || !subdivision?.name) {
            try {
                reverseGeocodeLocation = await (await fetch(
                    `/api/geocode?latitude=${latitude as number}&longitude=${longitude as number}`,
                )).json();
            } catch (e) {
                return null;
            }
        }

        return {
            provider: Provider.NETLIFY,
            latitude,
            longitude,
            city: reverseGeocodeLocation?.city || city,
            stateShort: reverseGeocodeLocation?.stateShort || subdivision?.code,
            country: getCountryISO3(country?.code) || country?.code,
            state: reverseGeocodeLocation?.state || subdivision?.name,
            franchiseId: null,
        };
    }
    return null;
};

// once we choose a location detection provider (and the fallback) will delete the one we don't use
const runNavigatorQuery = async (position: GeolocationPosition): Promise<UserLocationData | null> => {
    const { coords } = position;
    if (!coords || !coords?.latitude || !coords?.longitude) {
        return null;
    }

    if (coords.accuracy) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        window.browser_navigator_geolocation_accuracy = coords.accuracy;
    }

    try {
        return await (await fetch(
            `/api/geocode?latitude=${coords.latitude}&longitude=${coords.longitude}`,
        )).json();
    } catch (e) {
        return null;
    }
};

interface LocatorProviderReturnable {
    loadingFirst: boolean // Used to find out if we've loaded a first franchise - to cut down load times for Nearest CTA
    loading: boolean,
    geo: UserLocationData | null,
    nearby: UseNearbyFranchiseMultipleItems,
    franchise: UseNearbyFranchiseSingleItem,
    primaryFranchiseNumber: string | null,
    setFranchise: (
        newFranchise: FranchiseData,
        userLocation: SearchInputAddressComponents | null,
    ) => void,
    geoDerivedFromOverride: boolean, // remove this
    locationChangedByUser: boolean,
    isNationalCallCenter: boolean
    announcements: Announcement[]
}

// Force casting here to make sure context consumers will throw if used outside a provider.
const LocatorContext = React.createContext<LocatorProviderReturnable>(
    null as unknown as LocatorProviderReturnable,
);

// This function should probably be renamed, since it also bundles franchisees and can do overrides
const LocatorProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
    /*
    * Get localstorage if it's available,
    * else return null
    * */
    const {
        setData: setNavPromptShown,
        getData: getNavPromptShown,
    } = useLocalStorageState<boolean>(
        'navigator_prompt_shown',
        async (): Promise<boolean> => false,
        [],
        (state: boolean): number => {
            if (state) {
                return 72;
            }
            return 24;
        },
    );

    const {
        data: geoData,
        setData: setGeoData,
        loading: isLoading,
        getData: getSavedGeoBeforeFinishedLoading,
    } = useLocalStorageState<UserLocationDataSet | null>(
        LOCAL_GEO_KEY,
        async (): Promise<UserLocationDataSet> => {
            /*
            * This will run only on initial visit, or
            * again if the localStorage time has expired
            * */
            let locator = await runNetlifyQuery();

            if (!locator) {
                locator = await runIpStackQuery();
            }

            return {
                geolocationApi: locator || null,
                navigator: null,
                userSelectedFranchiseLocation: null,
                userSelectedGeoLocation: null,
                locationChangedByUser: false,
            };
        },
        [],
        (parsedData: UserLocationDataSet | null): number => {
            if (parsedData) {
                if (parsedData.locationChangedByUser) {
                    return 72;
                }
                return 24;
            }
            return 0;
        },
        (parsedData: UserLocationDataSet | null): boolean => !!parsedData?.locationChangedByUser,
    );

    const applyNavigatorQuery = React.useCallback(async (position: GeolocationPosition): Promise<void> => {
        const result = await runNavigatorQuery(position);
        if (result) {
            setGeoData({
                geolocationApi: geoData?.geolocationApi || null,
                navigator: { ...result, provider: Provider.NAVIGATOR, franchiseId: null },
                userSelectedFranchiseLocation: geoData?.userSelectedFranchiseLocation || null,
                userSelectedGeoLocation: geoData?.userSelectedGeoLocation || null,
                locationChangedByUser: geoData?.locationChangedByUser || false,
            });
        }
    }, [
        geoData?.locationChangedByUser,
        geoData?.geolocationApi,
        geoData?.userSelectedGeoLocation,
        geoData?.userSelectedFranchiseLocation,
        setGeoData,
    ]);

    // Get the most appropriate geo provider result
    const geoToUse = (() => {
        if (geoData?.locationChangedByUser && geoData?.userSelectedFranchiseLocation) {
            return geoData.userSelectedFranchiseLocation;
        }
        if (geoData?.navigator) {
            return geoData.navigator;
        }
        if (geoData?.geolocationApi) {
            return geoData?.geolocationApi;
        }
        return null;
    })();

    // Get the lat+lng from the most appropriate geo provider
    const nearbyQuery = geoToUse ? {
        latitude: Number(geoToUse.latitude),
        longitude: Number(geoToUse.longitude),
        franchiseId: geoToUse.franchiseId || null,
        provider: geoToUse.provider || null,
        country: geoToUse.country || null,
        locationType: null,
        zip: null,
    } : null;

    // Find franchisees based on the lat+lng available

    const {
        isLoading: isLoadingFranchises,
        data: nearbyFranchises,
        announcements,
        primaryFranchiseSummary,
        primaryFranchiseNumber,
    } = useNearbyFranchises(nearbyQuery);

    React.useEffect(() => {
        if (isLoading) {
            return;
        }

        const hasFirstFranchise = Boolean(
            primaryFranchiseSummary || nearbyFranchises?.[0]?.data,
        );
        if (!hasFirstFranchise && isLoadingFranchises) {
            return;
        }

        const geoDataToUse = geoData || getSavedGeoBeforeFinishedLoading();
        const navPromptShown = getNavPromptShown();
        if (!geoDataToUse || (!geoDataToUse?.navigator && !navPromptShown)) {
            setNavPromptShown(true);
            navigator.geolocation.getCurrentPosition(
                applyNavigatorQuery,
                (error) => {
                    if ('dataLayer' in window && Array.isArray(window.dataLayer) && !window.dataLayer.find(item => item.event === 'LocationPermission' && !item.allowed)) {
                        window.dataLayer.push({
                            event: 'LocationPermission',
                            allowed: false,
                            error: error.message,
                        });
                    }
                },
                {
                    enableHighAccuracy: false,
                },
            );
        }
    }, [
        applyNavigatorQuery,
        geoData,
        getNavPromptShown,
        getSavedGeoBeforeFinishedLoading,
        isLoading,
        isLoadingFranchises,
        nearbyFranchises,
        primaryFranchiseSummary,
        setNavPromptShown,
    ]);

    // User is choosing a different location to the ones selected for them,
    // Create their Geo data from the Yext of the franchise
    // Even though we type-check the Yext existing, it should always exist,
    // As we do not build any franchises or franchise json if the Yext is missing
    const setFranchiseExternal = React.useCallback((
        newFranchise: FranchiseData,
        userLocation: SearchInputAddressComponents | null,
    ): void => {
        if (newFranchise && newFranchise.franchiseNumber) {
            const userSelectionData = franchiseToGeo(newFranchise);

            if (userSelectionData) {
                const userSelectedGeoLocation: UserLocationData = {
                    city: userLocation?.city ?? userSelectionData.city,
                    country: userLocation?.country ?? userSelectionData.country,
                    franchiseId: null,
                    latitude: userLocation?.latitude ?? userSelectionData.latitude,
                    longitude: userLocation?.longitude ?? userSelectionData.longitude,
                    provider: Provider.FRANCHISE,
                    state: userLocation?.state ?? userSelectionData.state,
                    stateShort: userLocation?.stateShort ?? userSelectionData.stateShort,
                    zip: userLocation?.zip ?? userSelectionData.zip,
                };

                const data: UserLocationDataSet = {
                    geolocationApi: geoData?.geolocationApi || null,
                    navigator: geoData?.navigator || null,
                    userSelectedFranchiseLocation: userSelectionData,
                    userSelectedGeoLocation,
                    locationChangedByUser: true,
                };

                setGeoData(data);
            }
        }
    }, [geoData, setGeoData]);

    const value = React.useMemo(() => {
        const summaryFranchiseItem = primaryFranchiseSummary
            ? ({
                error: null,
                isLoading: isLoadingFranchises && !nearbyFranchises?.[0]?.data,
                isSuccess: Boolean(nearbyFranchises?.[0]?.data || primaryFranchiseSummary),
                data: primaryFranchiseSummary as unknown as FranchiseData,
            } as UseNearbyFranchiseSingleItem)
            : null;
        const resolvedFranchise = nearbyFranchises[0] || summaryFranchiseItem;
        const locatorData: LocatorProviderReturnable = {
            loadingFirst: (() => {
                if (isLoading) {
                    return true;
                }
                if (nearbyFranchises?.[0]?.data || primaryFranchiseSummary) {
                    return false;
                }
                return isLoadingFranchises;
            })(),
            loading: (() => {
                if (!geoToUse) {
                    return true;
                }
                return (isLoading || isLoadingFranchises);
            })(),
            geo: geoToUse || null,
            nearby: nearbyFranchises,
            primaryFranchiseNumber,
            franchise: resolvedFranchise || ({
                error: null,
                isLoading: isLoading || isLoadingFranchises,
                data: {
                    id: 'NATIONAL_CALL_CENTER', // This may get forwarded to Invoca?
                    name: 'National Call Center',
                    mainPhone: PHONE_NUMBER,
                },
            } as unknown as UseNearbyFranchiseSingleItem),
            setFranchise: setFranchiseExternal,
            geoDerivedFromOverride: false, // remove this
            locationChangedByUser: geoData?.locationChangedByUser || false,
            isNationalCallCenter: !resolvedFranchise && !isLoadingFranchises,
            announcements: announcements || [],
        };
        return locatorData;
    }, [
        geoData?.locationChangedByUser,
        geoToUse,
        isLoading,
        isLoadingFranchises,
        nearbyFranchises,
        primaryFranchiseNumber,
        primaryFranchiseSummary,
        setFranchiseExternal,
        announcements,
    ]);

    return (
        <LocatorContext.Provider value={value}>
            {children}
        </LocatorContext.Provider>
    );
};

export default LocatorProvider;

export const useLocator = (): LocatorProviderReturnable => {
    const data = React.useContext(LocatorContext);
    if (!data) {
        throw new Error('useLocator used outside of context');
    }
    return data;
};
